/* global google */
import React from 'react';
import './App.scss';
import {
  GoogleMap,
  InfoBox,
  KmlLayer,
  LoadScript,
  Marker,
  MarkerClusterer,
} from '@react-google-maps/api';
import axios from 'axios';
import type { Immo } from './Types';

function GMap(props) {
  const containerStyle = {
    width: '100%',
    height: '90vh',
    position: 'absolute',
    top: 0,
    left: 0,
  };

  const [map, setMap] = React.useState(null);

  const [center, setCenter] = React.useState({
    lat: 47.26958,
    lng: 15.01375,
  });
  const [openInfo, setOpenInfo] = React.useState(null);

  const onLoad = React.useCallback(
    function callback(map) {
      axios
        .post('https://www.reichert-immobilien.at/immobilien/api/v1/search?country=AUT')
        .then((res) => {
          if (res.data.results) {
            let dataArray = Object.values(res.data.results);
            dataArray = dataArray.filter((location: Immo) => {
              return location.geo.show;
            });
            props.setLocations(dataArray);
            props.setFilteredLocations(dataArray);
            setMap(map);
          } else {
            console.error(res.data.message);
          }
          props.setMeta(res.data.meta);
        });
    },
    [props]
  );

  const options = {
    styles: [
      {
        url: '/wp-content/plugins/reichert-gmaps/build/icons/house.svg',
        height: 48,
        width: 48,
        textSize: 15,
        anchorText: [15, 0],
        fontWeight: '700',
      },
    ],
    maxZoom: 17,
  };

  function locationThumbnail(location: Immo) {
    const image = (
      <img
        className={'custom-window-img'}
        src={location.thumbnail}
        alt={'a thumbnail'}
      />
    );
    if (
      location.thumbnail &&
      (location.is_top === 1 || location.is_premium === 1)
    ) {
      const markerCallout = (
        <div className={'marker-callout'}>
          {location.is_premium === 1
            ? 'PREMIUM'
            : location.is_top === 1
            ? 'TOP'
            : ''}
        </div>
      );
      return [image, markerCallout];
    } else if (location.thumbnail) {
      return image;
    }
    return '';
  }

  function getIconForLocation(location: Immo) {
    if (location.is_premium === 1) {
      return location.geo.level < 2
        ? {
            url:
              '/wp-content/plugins/reichert-gmaps/build/icons/precise-premium.svg',
            scaledSize: new google.maps.Size(48, 48),
          }
        : {
            url:
              '/wp-content/plugins/reichert-gmaps/build/icons/approximate-premium.svg',
            scaledSize: new google.maps.Size(40, 40),
          };
    }
    return location.geo.level < 2
      ? {
          url: '/wp-content/plugins/reichert-gmaps/build/icons/precise.svg',
          scaledSize: new google.maps.Size(48, 48),
        }
      : {
          url: '/wp-content/plugins/reichert-gmaps/build/icons/approximate.svg',
          scaledSize: new google.maps.Size(40, 40),
        };
  }

  const kmlOptions = {
    preserveViewport: true,
    clickable: false,
    screenOverlays: false,
    suppressInfoWindows: true,
  };

  function priceShouldRender(location: Immo): boolean {
    return location.rent !== 1 && parseFloat(location.purchase_price) !== 0;
  }

  return (
    <LoadScript googleMapsApiKey={props.settings.api_key}>
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={center}
        zoom={9}
        onLoad={onLoad}
        onClick={() => {
          setOpenInfo(null);
        }}
        clickableIcons={false}
        options={{
          fullscreenControl: false,
          streetViewControl: false,
          styles: JSON.parse(
            '[{"stylers":[{"visibility":"simplified"}]},{"featureType":"administrative","stylers":[{"visibility":"on"}]},{"featureType":"administrative.country","stylers":[{"visibility":"on"}]},{"featureType":"administrative.land_parcel","stylers":[{"visibility":"on"}]},{"featureType":"administrative.land_parcel","elementType":"geometry","stylers":[{"visibility":"simplified"}]},{"featureType":"administrative.locality","stylers":[{"visibility":"on"}]},{"featureType":"administrative.neighborhood","stylers":[{"visibility":"on"}]},{"featureType":"administrative.province","stylers":[{"visibility":"on"}]},{"featureType":"landscape.man_made","stylers":[{"visibility":"simplified"}]},{"featureType":"landscape.man_made","elementType":"geometry.fill","stylers":[{"visibility":"off"}]},{"featureType":"landscape.man_made","elementType":"geometry.stroke","stylers":[{"visibility":"simplified"}]},{"featureType":"landscape.natural","stylers":[{"visibility":"simplified"}]},{"featureType":"poi","stylers":[{"visibility":"simplified"}]},{"featureType":"poi","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"poi.attraction","stylers":[{"visibility":"simplified"}]},{"featureType":"poi.attraction","elementType":"labels.text","stylers":[{"visibility":"simplified"}]},{"featureType":"poi.business","stylers":[{"visibility":"off"}]},{"featureType":"poi.government","stylers":[{"visibility":"off"}]},{"featureType":"poi.medical","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"road","elementType":"labels.icon","stylers":[{"visibility":"off"}]},{"featureType":"road.highway","stylers":[{"visibility":"simplified"}]},{"featureType":"road.highway","elementType":"labels","stylers":[{"visibility":"off"}]},{"featureType":"transit","stylers":[{"visibility":"simplified"}]},{"featureType":"transit.line","stylers":[{"visibility":"simplified"}]},{"featureType":"transit.station","stylers":[{"visibility":"simplified"}]},{"featureType":"transit.station","elementType":"labels.text","stylers":[{"visibility":"off"}]},{"featureType":"water","stylers":[{"visibility":"simplified"}]},{"featureType":"water","elementType":"labels.text","stylers":[{"visibility":"off"}]}]'
          ),
        }}
      >
        <KmlLayer
          url="https://reichert-immobilien.at/wp-content/plugins/reichert-gmaps/build/grz.kmz"
          options={kmlOptions}
        />
        <KmlLayer
          url="https://reichert-immobilien.at/wp-content/plugins/reichert-gmaps/build/stm.kmz"
          options={kmlOptions}
        />

        <MarkerClusterer averageCenter options={options}>
          {(clusterer) =>
            props.filteredLocations.map((location: Immo) => (
              <Marker
                key={location.id}
                position={{
                  lat: parseFloat(location.geo.lat) + Math.random() * 0.0004,
                  lng: parseFloat(location.geo.lon) + Math.random() * 0.0004,
                }}
                clusterer={clusterer}
                onClick={() => {
                  setCenter({
                    lat: parseFloat(location.geo.lat),
                    lng: parseFloat(location.geo.lon),
                  });
                  setOpenInfo(location);
                }}
                icon={getIconForLocation(location)}
              >
                {openInfo === location && (
                  <InfoBox
                    options={{ pixelOffset: new google.maps.Size(-125, 0) }}
                    anchor={location.geo}
                    id={location.openimmo_idid}
                  >
                    <div>
                      {locationThumbnail(location)}
                      <div className={'custom-window-content'}>
                        <div className={'custom-window-info'}>
                          {location.rent === 1 ? 'Mietobjekt' : 'Kaufobjekt'}
                        </div>
                        <strong className={'location-title'}>
                          {location.title}
                        </strong>
                        <br />
                        <div className={'custom-window-info'}>
                          {new Intl.NumberFormat("de-DE").format(location.area)}㎡ | {location.rooms} Zimmer
                          <br />
                          {priceShouldRender(location) && (
                            <p>{new Intl.NumberFormat("de-DE", {
                              style: "currency",
                              currency: "EUR"
                            }).format(location.purchase_price)}</p>
                          )}
                        </div>
                        {!priceShouldRender(location) && (
                          <div className={'custom-window-help'}>
                            <a
                              href={props.meta.immo_detail_url + location.slug}
                              style={{ color: '#EEC45A' }}
                            >
                              Preis- und Detailinformationen
                              <br />
                              der Immobilie anzeigen
                            </a>
                          </div>
                        )}
                      </div>
                      <a
                        className={'custom-window-button'}
                        type={'button'}
                        style={{ backgroundColor: '#EEC45A' }}
                        href={props.meta.immo_detail_url + location.slug}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <svg
                          width="32px"
                          height="32px"
                          viewBox="0 0 16 16"
                          className="bi bi-search"
                          fill="currentColor"
                          xmlns="http:/www.w3.org/2000/svg"
                          style={{ margin: '0 auto' }}
                        >
                          <path
                            fillRule="evenodd"
                            d="M10.442 10.442a1 1 0 0 1 1.415 0l3.85 3.85a1 1 0 0 1-1.414 1.415l-3.85-3.85a1 1 0 0 1 0-1.415z"
                          />
                          <path
                            fillRule="evenodd"
                            d="M6.5 12a5.5 5.5 0 1 0 0-11 5.5 5.5 0 0 0 0 11zM13 6.5a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0z"
                          />
                        </svg>
                      </a>
                    </div>
                  </InfoBox>
                )}
              </Marker>
            ))
          }
        </MarkerClusterer>
      </GoogleMap>
    </LoadScript>
  );
}

export default React.memo(GMap);
